/**
 * Sends buying journey events directly to Meta Pixel (fbq). No GTM configuration needed.
 * Respects marketing consent and production domain. Meta Pixel must be loaded (e.g. via GTM).
 */

import { GTM_ALLOWED_HOSTNAMES } from '@/config/gtm'
import { isYouthEnvironment } from '@/config/youthEnvironment'
import consentService from '@/services/cookiesPolicy/consentService'
import { COOKIE_CATEGORIES } from '@/constants/cookieCategories'
import {
  BUYING_JOURNEY_EVENTS,
  JOURNEY_FUNNEL_EVENTS
} from '@/constants/buyingJourney'

const META_EVENT_VIEW_CONTENT = 'ViewContent'
const META_EVENT_LEAD = 'Lead'
const META_EVENT_COMPLETE_REGISTRATION = 'CompleteRegistration'
const META_EVENT_INITIATE_CHECKOUT = 'InitiateCheckout'
const META_EVENT_PURCHASE = 'Purchase'

const ALLOW_LOCALHOST_FOR_META_TESTING = false
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || ''

let localhostPixelLoadStarted = false

function ensureMetaPixelLoadedOnLocalhost() {
  if (localhostPixelLoadStarted || !META_PIXEL_ID) return
  const hostname =
    typeof window !== 'undefined' ? window.location?.hostname : ''
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  if (!isLocalhost || !ALLOW_LOCALHOST_FOR_META_TESTING) return
  localhostPixelLoadStarted = true
  if (typeof window.fbq === 'function') return

  window.fbq = function () {
    ;(window.fbq.queue = window.fbq.queue || []).push(arguments)
  }
  window.fbq.queue = []
  window.fbq('init', META_PIXEL_ID)
  window.fbq('track', 'PageView')

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
}

function isHostAllowed(hostname) {
  if (GTM_ALLOWED_HOSTNAMES.includes(hostname)) return true
  if (
    ALLOW_LOCALHOST_FOR_META_TESTING &&
    (hostname === 'localhost' || hostname === '127.0.0.1')
  )
    return true
  return false
}

function getMetaPixelDebugStatus() {
  if (typeof window === 'undefined')
    return { allowed: false, reason: 'no_window' }
  if (isYouthEnvironment())
    return { allowed: false, reason: 'youth_environment' }
  const hostname = window.location?.hostname
  if (!hostname) return { allowed: false, reason: 'no_hostname' }
  if (!isHostAllowed(hostname))
    return { allowed: false, reason: 'domain_not_allowed', hostname }
  const hasMarketingConsent = consentService.isConsentedForCategory(
    COOKIE_CATEGORIES.MARKETING
  )
  if (!hasMarketingConsent)
    return { allowed: false, reason: 'no_marketing_consent', hostname }
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  if (!isLocalhost && typeof window.fbq !== 'function')
    return { allowed: false, reason: 'fbq_not_loaded', hostname }
  if (
    isLocalhost &&
    ALLOW_LOCALHOST_FOR_META_TESTING &&
    typeof window.fbq !== 'function'
  ) {
    if (META_PIXEL_ID) {
      ensureMetaPixelLoadedOnLocalhost()
    }
    return { allowed: false, reason: 'fbq_not_loaded', hostname }
  }
  const fbqExists = typeof window.fbq === 'function'
  if (!fbqExists) return { allowed: false, reason: 'fbq_not_loaded', hostname }
  return { allowed: true, reason: 'ok', hostname }
}

function isAllowed() {
  return getMetaPixelDebugStatus().allowed
}

function sendMetaEvent(standardEventName, params = {}) {
  if (!getMetaPixelDebugStatus().allowed) return
  try {
    window.fbq('track', standardEventName, params)
  } catch (_) {}
}

function sendMetaCustomEvent(customEventName, params = {}) {
  if (!getMetaPixelDebugStatus().allowed) return
  try {
    window.fbq('trackCustom', customEventName, params)
  } catch (_) {}
}

const JOURNEY_EVENT_TO_META = {
  [BUYING_JOURNEY_EVENTS.PLAN_PAGE_VIEW]: META_EVENT_VIEW_CONTENT,
  [BUYING_JOURNEY_EVENTS.PLAN_SELECTED]: META_EVENT_LEAD,
  [BUYING_JOURNEY_EVENTS.CHECKOUT_REDIRECT]: META_EVENT_INITIATE_CHECKOUT,
  [BUYING_JOURNEY_EVENTS.PAYMENT_SUCCESS]: META_EVENT_PURCHASE,
  [JOURNEY_FUNNEL_EVENTS.LANDING_VIEW]: META_EVENT_VIEW_CONTENT,
  [JOURNEY_FUNNEL_EVENTS.PRICING_VIEW]: META_EVENT_VIEW_CONTENT,
  [JOURNEY_FUNNEL_EVENTS.PLAN_PAGE_VIEW]: META_EVENT_VIEW_CONTENT,
  [JOURNEY_FUNNEL_EVENTS.PLAN_SELECTED]: META_EVENT_LEAD,
  [JOURNEY_FUNNEL_EVENTS.SIGNUP_START]: META_EVENT_LEAD,
  [JOURNEY_FUNNEL_EVENTS.SIGNUP_COMPLETE]: META_EVENT_COMPLETE_REGISTRATION,
  [JOURNEY_FUNNEL_EVENTS.CHECKOUT_REDIRECT]: META_EVENT_INITIATE_CHECKOUT,
  [JOURNEY_FUNNEL_EVENTS.PAYMENT_SUCCESS]: META_EVENT_PURCHASE,
  [JOURNEY_FUNNEL_EVENTS.CTA_CLICK]: META_EVENT_LEAD,
  [JOURNEY_FUNNEL_EVENTS.RECURRING_PAYMENT_SUCCEEDED]: META_EVENT_PURCHASE
}

export function sendBuyingJourneyToMeta(payload) {
  const { journey_event: journeyEvent, journey_plan: plan, ...rest } = payload
  const metaEvent = JOURNEY_EVENT_TO_META[journeyEvent]
  if (metaEvent) {
    const params = {
      content_name: plan || journeyEvent,
      ...rest
    }
    if (metaEvent === META_EVENT_PURCHASE) {
      params.currency = params.currency || 'USD'
    }
    sendMetaEvent(metaEvent, params)
  } else if (
    journeyEvent === BUYING_JOURNEY_EVENTS.PAYMENT_CANCEL ||
    journeyEvent === JOURNEY_FUNNEL_EVENTS.PAYMENT_CANCEL
  ) {
    sendMetaCustomEvent('CheckoutCancelled', { journey_plan: plan, ...rest })
  } else if (journeyEvent) {
    const params = {
      content_name: plan || journeyEvent,
      ...rest
    }
    sendMetaCustomEvent(journeyEvent, params)
  }
}

export default {
  sendBuyingJourneyToMeta,
  isAllowed,
  getMetaPixelDebugStatus
}
